import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import './ProductInfoCard.css';
import useAuth from '../../hooks/useAuth';
import LoginSignup from '../../Pages/LoginSignup';
import { startRedsysCheckout } from '../../utils/redsysCheckout';

function ProductInfoCard({ product, handleClosePopup, showPopup }) {
  const navigate = useNavigate();
  const { refreshUserData } = useAuth(false);
  const [showLogin, setShowLogin] = useState(false);
  const [hasAccess, setHasAccess] = useState(null); // null=checking, true=access, false=no access
  const [accessMessage, setAccessMessage] = useState('');
  const [isCheckingAccess, setIsCheckingAccess] = useState(false);

  // Reset state when the popup closes
  useEffect(() => {
    if (!showPopup) {
      setAccessMessage('');
      setHasAccess(null);
      setIsCheckingAccess(false);
      setShowLogin(false);
    }
  }, [showPopup]);

  // Auto-check permissions when popup opens
  useEffect(() => {
    if (!showPopup || !product) return;

    const authToken = localStorage.getItem('auth-token');
    if (!authToken) {
      setHasAccess(null);
      return;
    }

    const autoCheck = async () => {
      setIsCheckingAccess(true);
      try {
        const response = await fetch(
          `${process.env.REACT_APP_BACKEND_URL}/permissions/check/${product._id}`,
          { headers: { 'auth-token': authToken } }
        );
        if (response.ok) {
          const data = await response.json();
          if (data.hasPermission) {
            setHasAccess(true);
          } else if (data.purchasePending) {
            setHasAccess(false);
            setAccessMessage('Your purchase is pending approval.');
          } else {
            setHasAccess(false);
          }
        } else {
          setHasAccess(false);
        }
      } catch (error) {
        console.error('Error checking permissions:', error);
        setHasAccess(null);
      } finally {
        setIsCheckingAccess(false);
      }
    };

    autoCheck();
  }, [showPopup, product]);

  // Push history entry when card opens, close on back gesture/button
  useEffect(() => {
    if (!showPopup) return;

    window.history.pushState({ productCard: true }, '');

    const handlePopState = (e) => {
      if (handleClosePopup) handleClosePopup();
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [showPopup, handleClosePopup]);

  const handlePlayButtonClick = async () => {
    const authToken = localStorage.getItem('auth-token');
    if (!authToken) {
      setShowLogin(true);
      return;
    }
    navigate(`/product/${product._id}`);
    handleClosePopup?.();
  };

  const handleLoginSuccess = async () => {
    await refreshUserData();
    setShowLogin(false);
    // Re-check permissions after login
    setHasAccess(null);
    setIsCheckingAccess(true);
    try {
      const authToken = localStorage.getItem('auth-token');
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/permissions/check/${product._id}`,
        { headers: { 'auth-token': authToken } }
      );
      if (response.ok) {
        const data = await response.json();
        if (data.hasPermission) {
          setHasAccess(true);
        } else if (data.purchasePending) {
          setHasAccess(false);
          setAccessMessage('Your purchase is pending approval.');
        } else {
          setHasAccess(false);
        }
      } else {
        setHasAccess(false);
      }
    } catch (error) {
      setHasAccess(null);
    } finally {
      setIsCheckingAccess(false);
    }
  };

  const handleBuyNow = async () => {
    // Card payment enabled for this product -> Redsys checkout (redirects away).
    if (product.cardPaymentEnabled) {
      const result = await startRedsysCheckout(product._id);
      if (result.needLogin) {
        setShowLogin(true);
      } else if (result.error) {
        setAccessMessage(result.error);
      }
      return;
    }

    // Otherwise the manual flow: create a pending purchase for admin approval.
    const authToken = localStorage.getItem('auth-token');
    if (!authToken) {
      setShowLogin(true);
      return;
    }
    try {
      const purchaseResponse = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/cart/purchase`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'auth-token': authToken,
          },
          body: JSON.stringify({ productId: product._id, price: product.price }),
        }
      );
      const purchaseResult = await purchaseResponse.json();
      if (purchaseResult.success) {
        await refreshUserData();
        setHasAccess(false);
        setAccessMessage('Purchase successful! Your access is pending approval.');
      } else {
        setAccessMessage(purchaseResult.message || 'Purchase failed. Please try again.');
      }
    } catch (error) {
      console.error('Error completing purchase:', error);
      setAccessMessage('An error occurred. Please try again.');
    }
  };

  if (!product) return null;

  const event = product.event;
  const projections = event?.video_types || [];
  const camNames = event?.cam_names || [];
  const audioNames = event?.audio_names || [];
  const hasWebRTC = !!event?.webrtc_manifest;

  const normalizeProjection = (p) => {
    const upper = (p || '').toUpperCase();
    if (upper === 'NONE') return '2D';
    if (upper === '360' || upper === '360_TB') return '360';
    if (upper === '180' || upper === '180_NONO') return '180';
    return '2D';
  };

  const uniqueProjections = [...new Set(projections.map(normalizeProjection))].filter(Boolean);
  const hasMeta = uniqueProjections.length > 0 || camNames.length > 0 || audioNames.length > 0 || hasWebRTC;

  if (!showPopup) return null;

  return ReactDOM.createPortal(
    <div className="product-info-card-popup" onClick={handleClosePopup}>
      <div className="product-info-card-content" onClick={(e) => e.stopPropagation()}>
        {handleClosePopup && (
          <button
            className="product-info-card-close-button"
            onClick={handleClosePopup}
          >
            ✕
          </button>
        )}
        {showLogin ? (
          <LoginSignup
            onLoginSuccess={handleLoginSuccess}
            onClose={() => setShowLogin(false)}
            showCloseButton
            isInline
          />
        ) : (
          <>
            <div className="product-info-card-banner">
              <img
                src={product.featuredImage}
                alt={product.title}
                className="product-info-card-image"
              />
              <div className="product-info-card-banner-gradient" />
            </div>

            <div className="product-info-card-body">
              <h3 className="product-info-card-title">{product.title}</h3>

              {product.description && (
                <p className="product-info-card-description">{product.description}</p>
              )}

              {hasMeta && (
                <div className="product-info-card-meta">
                  {uniqueProjections.map((proj) => (
                    <span key={proj} className={`meta-tag meta-tag-proj-${proj === '360' ? '360' : proj === '180' ? '180' : '2d'}`}>{proj}</span>
                  ))}
                  {hasWebRTC && (
                    <span className="meta-tag meta-tag-webrtc">WebRTC</span>
                  )}
                  {camNames.length > 0 && (
                    <span className="meta-tag meta-tag-cam">
                      {camNames.length} {camNames.length === 1 ? 'Camera' : 'Cameras'}
                    </span>
                  )}
                  {audioNames.length > 0 && (
                    <span className="meta-tag meta-tag-audio">
                      {audioNames.length} Audio
                    </span>
                  )}
                </div>
              )}

              <div className="product-info-card-actions">
                {isCheckingAccess ? (
                  <p className="product-info-card-loading-message">Checking access...</p>
                ) : hasAccess === true ? (
                  <button
                    className="product-info-card-play-button"
                    onClick={handlePlayButtonClick}
                  >
                    ▶ Play Now
                  </button>
                ) : hasAccess === false && accessMessage ? (
                  <p className="product-info-card-alert-message">{accessMessage}</p>
                ) : hasAccess === false ? (
                  <button
                    className="product-info-card-buy-now"
                    onClick={handleBuyNow}
                  >
                    <span>Purchase Now</span>
                  </button>
                ) : (
                  <button
                    className="product-info-card-play-button"
                    onClick={handlePlayButtonClick}
                  >
                    ▶ Play Now
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>,
    document.body
  );
}

export default ProductInfoCard;
